import { useRef, useMemo, useEffect, useCallback, forwardRef } from 'react';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';
import { FaCalendar } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { Typography, TextField, Button as MuiButton } from '@material-ui/core';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { makeStyles } from '@material-ui/core/styles';
import FusePageCarded from '@fuse/core/FusePageCarded/FusePageCarded';
import { useHistory } from 'react-router-dom';
import { BsGearFill, BsCheckCircleFill } from 'react-icons/bs';
import { AiOutlineDisconnect } from 'react-icons/ai';

import Sidebar from 'app/admin/common/component/Sidebar';
import PipelineConnectorOverlay from './PipelineConnectorOverlay';
import UnitDetailsIndicator from './UnitDetailsIndicator';
import UnitDetailsPopup from './UnitDetailsPopup';
import UnitDetailsSidebar from './UnitDetailsSidebar';
import UnitHistoryPopup from './UnitHistoryPopup';
import DataValidationIndicator from './DataValidationIndicator';
import DataValidationDetailsPopup from './DataValidationDetailsPopup';
import DataValidationHistoryPopup from './DataValidationHistoryPopup';
import ModelValidationHistoryPopup from './ModelValidationHistoryPopup';
import ModelValidationDetailsSidebar from './ModelValidationDetailsSidebar';
import OptimizerDetailsSidebar from './OptimizerDetailsSidebar';
import DynamicValidationPopup from './DynamicValidationPopup';
import DynamicValidationSettingsPopup from './DynamicValidationSettingsPopup';
import DynamicValidationDetailsPopup from './DynamicValidationDetailsPopup';
import DynamicValidationHistoryPopup from './DynamicValidationHistoryPopup';
import ApprovePopup from './ApprovePopup';
import UnitOptimizerHistoryPopup from './UnitOptimizerHistoryPopup';

import { isUserOperator } from 'app/admin/common/util/CommonUtil';
import PageHeaderBackButton from 'modules/ui/component/PageHeaderBackButton';
import { useOptimizationPipeline } from '../OptimizationPipelineProvider';

const GRID = { rows: 5, cols: 8 };
const NODE_REGISTRY = { unit: 'Unit', dataValidation: 'DataValidation', modelValidation: 'ModelValidation', optimizerInject: 'OptimizerInject', optimizer: 'Optimizer', approve: 'Approve', run: 'Run' };
const REGISTRY = { Unit, DataValidation, ModelValidation, OptimizerInject, Optimizer, Approve, Run };

const NODES_CORE = [
    { id: 'n1', key: 'unit', title: 'Unit', row: 2, col: 1, w: 2, h: 2, heightMode: 'fit' },
    { id: 'n2', key: 'dataValidation', title: 'Data Validation', row: 2, col: 3, w: 2, h: 2, heightMode: 'auto' },
    { id: 'n3', key: 'modelValidation', title: 'Model Validation', row: 2, col: 5, w: 2, h: 2, heightMode: 'auto' },
    { id: 'n5', key: 'optimizer', title: 'Unit Optimizer', row: 2, col: 7, w: 2, h: 2, heightMode: 'fit' },
    { id: 'n6', key: 'approve', row: 4, col: 4, w: 2, h: 2, heightMode: 'auto' },
];

const NODES_WITH_RUN = [
    { id: 'n1', key: 'unit', title: 'Unit', row: 2, col: 1, w: 2, h: 2, heightMode: 'fit' },
    { id: 'n2', key: 'dataValidation', title: 'Data Validation', row: 2, col: 3, w: 2, h: 2, heightMode: 'auto' },
    { id: 'n3', key: 'modelValidation', title: 'Model Validation', row: 2, col: 5, w: 2, h: 2, heightMode: 'auto' },
    { id: 'n5', key: 'optimizer', title: 'Unit Optimizer', row: 2, col: 7, w: 2, h: 2, heightMode: 'fit' },
    { id: 'n6', key: 'run', title: 'EDT Dynamic Check', row: 4, col: 5, w: 2, h: 2, heightMode: 'auto' },
    { id: 'n7', key: 'approve', row: 4, col: 3, w: 2, h: 2, heightMode: 'auto' },
];

const CONNECTIONS_BASE = [
    { from: 'n1', to: 'n2', fromSide: 'right', toSide: 'left' },
    { from: 'n2', to: 'n3', fromSide: 'right', toSide: 'left' },
    { from: 'n3', to: 'n5', fromSide: 'right', toSide: 'left' },
    { from: 'n4', to: 'n5', fromSide: 'bottom', toSide: 'top' },
    { from: 'n5', to: 'n6', fromSide: 'bottom', toSide: 'right', route: 'vfirst' },
    { from: 'n6', to: 'n1', fromSide: 'left', toSide: 'bottom' },
];

const CONNECTIONS_WITH_RUN = [
    { from: 'n1', to: 'n2', fromSide: 'right', toSide: 'left' },
    { from: 'n2', to: 'n3', fromSide: 'right', toSide: 'left' },
    { from: 'n3', to: 'n5', fromSide: 'right', toSide: 'left' },
    { from: 'n4', to: 'n5', fromSide: 'bottom', toSide: 'top' },
    { from: 'n5', to: 'n6', fromSide: 'bottom', toSide: 'right', route: 'vfirst' },
    { from: 'n6', to: 'n7', fromSide: 'left', toSide: 'right' },
    { from: 'n7', to: 'n1', fromSide: 'left', toSide: 'bottom', route: 'hfirst' },
];

export default function Pipeline() {
    const { id, sidebar, optimizationPipelineData, ts, setTs, isDataValidationSkipped } = useOptimizationPipeline();

    const history = useHistory();
    const user = useSelector(({ auth }) => auth.user);
    const isOperator = isUserOperator(user);

    const wrapperRef = useRef(null);
    const nodeRefs = useRef({});
    const anchorRefs = useRef({});

    const hasEdtDynamicCheck = !!optimizationPipelineData?.edtDynamicCheck;
    const hasPiWriter = !!optimizationPipelineData?.piWriter;
    const noPiWriter = !hasPiWriter;

    const NODES_TO_RENDER = useMemo(() => {
        const baseNodes = hasEdtDynamicCheck ? NODES_WITH_RUN : NODES_CORE;

        return baseNodes.map((node) => {
            if (node.key === 'approve' && noPiWriter) {
                return {
                    ...node,
                    renderAsIcon: true
                };
            }

            return node;
        });
    }, [hasEdtDynamicCheck, noPiWriter]);

    const CONNECTIONS_TO_RENDER = useMemo(() => {
        const baseConnections = hasEdtDynamicCheck ? CONNECTIONS_WITH_RUN : CONNECTIONS_BASE;
        const approveId = hasEdtDynamicCheck ? 'n7' : 'n6';

        return baseConnections.map((connection) => {
            if (!noPiWriter) return connection;

            if (connection.from === approveId || connection.to === approveId) {
                return {
                    ...connection,
                    dashed: true
                };
            }

            return connection;
        });
    }, [hasEdtDynamicCheck, noPiWriter]);

    const nodeById = useMemo(() => Object.fromEntries(NODES_TO_RENDER.map(n => [n.id, n])), [NODES_TO_RENDER]);

    const handleTsChange = useCallback((newDate) => { setTs(newDate); }, [setTs]);

    if (!optimizationPipelineData) return null;

    return (
        <>

            <Sidebar>
                {sidebar === 'OPTIMIZATION_DETAILS' && <OptimizerDetailsSidebar />}
                {sidebar === 'UNIT_MEASUREMENT' && <UnitDetailsSidebar />}
                {sidebar === 'MODEL_VALIDATION_LOG' && <ModelValidationDetailsSidebar />}
            </Sidebar>

            <FusePageCarded
                classes={{ toolbar: 'p-0', header: 'min-h-72 h-72 sm:h-136 sm:min-h-136' }}
                header={
                    <div className='flex items-center w-full max-w-full'>
                        <div className='w-full flex justify-between'>
                            <div className='flex flex-col items-start'>
                                <PageHeaderBackButton to="/optimization-pipeline" />
                                <Typography className='hidden sm:flex text-16 md:text-24 mx-12 font-semibold'>{optimizationPipelineData?.name || 'Optimization Pipeline'}</Typography>
                            </div>
                            <div className='flex items-center gap-16'>
                                <div className='flex items-center gap-4'>
                                    {!isOperator && <MuiButton className="whitespace-nowrap mx-4" variant="contained" color="secondary" onClick={() => { history.push(`/optimization-pipeline/edit/${id}`); }} startIcon={<BsGearFill className="w-16 h-16 hidden sm:flex" />}>Settings</MuiButton>}
                                </div>
                                <HeaderDateTime value={ts} onChange={handleTsChange} />
                            </div>
                        </div>
                    </div>
                }
                content={
                    <div className='w-full flex-1 flex items-center justify-center'>

                        <div
                            ref={wrapperRef}
                            className='w-full grid relative gap-16 sm:gap-32 lg:gap-64 2xl:gap-96'
                            style={{ maxWidth: '1380px', gridTemplateColumns: `repeat(${GRID.cols}, minmax(0, 1fr))`, gridTemplateRows: `repeat(${GRID.rows}, auto)` }}
                        >

                            <PipelineConnectorOverlay
                                wrapperRef={wrapperRef}
                                nodeRefs={nodeRefs}
                                anchorRefs={anchorRefs}
                                nodes={NODES_TO_RENDER}
                                nodeById={nodeById}
                                connections={CONNECTIONS_TO_RENDER}
                            />

                            {NODES_TO_RENDER.map((n) => {
                                const CompName = NODE_REGISTRY[n.key];
                                const Comp = REGISTRY[CompName];
                                if (!Comp && !n.renderAsIcon) return null;

                                const cellStyle = {
                                    gridColumn: `${n.col} / span ${n.w}`,
                                    gridRow: `${n.row} / span ${n.h}`,
                                    alignSelf: 'stretch'
                                };

                                const disabledBySkip = isDataValidationSkipped && (
                                    n.key === 'modelValidation' ||
                                    n.key === 'optimizer' ||
                                    n.key === 'approve' ||
                                    n.key === 'run'
                                );

                                return (
                                    <div
                                        key={n.id}
                                        className='p-16 flex items-center justify-center'
                                        style={cellStyle}
                                        ref={(el) => { if (el) nodeRefs.current[n.id] = el; }}
                                        data-node-id={n.id}
                                    >
                                        {n.renderAsIcon ? (
                                            <ApproveIcon
                                                disabled={disabledBySkip}
                                                anchorRef={(el) => { if (el) anchorRefs.current[n.id] = el; }}
                                            />
                                        ) : (
                                            <NodeBox title={n.title} fit={(n.heightMode ?? 'auto') === 'fit'} disabled={disabledBySkip}>
                                                <Comp />
                                            </NodeBox>
                                        )}
                                    </div>
                                );
                            })}

                        </div>

                    </div>
                }
            />

        </>
    );
}

function NodeBox({ title, children, fit, disabled }) {
    return (
        <div className={`w-full ${fit ? 'h-full' : 'h-auto'} flex flex-col items-start rounded-2xl shadow-lg bg-gray-50 border overflow-hidden` + (disabled ? ' opacity-60 grayscale pointer-events-none' : '')}>
            {title && <div className='border-b border-r bg-white rounded-br-2xl lg:text-lg lg:leading-normal font-bold px-16 py-12'>{title}</div>}
            <div className='w-full flex-1 justify-center flex flex-col gap-8 p-12'>{children}</div>
        </div>
    );
}

function ApproveIcon({ disabled, anchorRef }) {
    return (
        <div className={'w-full h-full min-h-[96px] flex items-center justify-center' + (disabled ? ' opacity-60 grayscale pointer-events-none' : '')}>
            <div ref={anchorRef} className='w-56 sm:w-64 md:w-72 h-16 flex items-center justify-center'>
                <AiOutlineDisconnect className='text-40 sm:text-44 text-grey-300 drop-shadow-md' />
            </div>
        </div>
    );
}

function Unit() {
    const { popup, setPopup, setSidebar, ts, mergedMeasurementDataList } = useOptimizationPipeline();

    function getDataQualityForTs(list) {
        if (!Array.isArray(list) || !ts) return 'MISSING';
        const target = (ts instanceof Date) ? ts.getTime() : new Date(ts).getTime();
        if (Number.isNaN(target)) return 'MISSING';
        const entry = list.find(e => {
            const t = e?.ts ? new Date(e.ts).getTime() : NaN;
            return !Number.isNaN(t) && t === target;
        });
        const synth = entry?.meta?.synthetic_data;
        if (synth === true) return 'SYNTH';
        if (synth === false) return 'MEASURED';
        return 'MISSING';
    }

    const dataQualityDict = { 'MISSING': 'Missing', 'SYNTH': 'Synthetic', 'MEASURED': 'Measured' };

    return (
        <>
            {popup === 'UNIT_DETAILS' && <UnitDetailsPopup />}
            {popup === 'UNIT_HISTORY' && <UnitHistoryPopup />}

            <ValueIndicator
                label='Data Type'
                value={dataQualityDict[getDataQualityForTs(mergedMeasurementDataList) || 'MISSING']}
                positive={getDataQualityForTs(mergedMeasurementDataList) === 'MEASURED'}
                negative={getDataQualityForTs(mergedMeasurementDataList) === 'MISSING'}
                warning={getDataQualityForTs(mergedMeasurementDataList) === 'SYNTH'}
            />

            <UnitDetailsIndicator />
            <Button label='Show Data' onClick={() => setSidebar('UNIT_MEASUREMENT')} />
            <Button label='History' onClick={() => setPopup('UNIT_HISTORY')} />
            <TsIndicator ts={ts} />
        </>
    );
}

function DataValidation() {
    const { popup, setPopup, ts, mergedMeasurementDataList } = useOptimizationPipeline();

    function hasOutlierForTs(list) {
        if (!Array.isArray(list) || !ts) return undefined;
        const target = (ts instanceof Date) ? ts.getTime() : new Date(ts).getTime();
        if (Number.isNaN(target)) return undefined;
        const entry = list.find(e => {
            const t = e?.ts ? new Date(e.ts).getTime() : NaN;
            return !Number.isNaN(t) && t === target;
        });
        return (typeof entry?.meta?.outlier_detection?.outlier === 'boolean') ? entry.meta.outlier_detection.outlier : undefined;
    }

    return (
        <>
            {popup === 'DATA_VALIDATION_DETAILS' && <DataValidationDetailsPopup />}
            {popup === 'DATA_VALIDATION_HISTORY' && <DataValidationHistoryPopup />}

            <BooleanIndicator label='Outlier Result ' value={hasOutlierForTs(mergedMeasurementDataList)} reversed />
            <DataValidationIndicator />
            <Button label='History' onClick={() => setPopup('DATA_VALIDATION_HISTORY')} />
            <TsIndicator ts={ts} />
        </>
    );
}

function ModelValidation() {
    const { popup, setPopup, setSidebar, modelValidationData, modelValidationLogDataList, ts } = useOptimizationPipeline();

    const history = useHistory();

    function getModelValidationInfo(list) {
        if (!Array.isArray(list) || !ts) return { accurate: undefined, message: null };

        const target = (ts instanceof Date) ? ts.getTime() : new Date(ts).getTime();
        if (Number.isNaN(target)) return { accurate: undefined, message: null };

        const entry = list.find(e => {
            const t = e?.ts ? new Date(e.ts).getTime() : NaN;
            return !Number.isNaN(t) && t === target;
        });

        if (!entry) return { accurate: undefined, message: null };

        const normalizeAccurate = (v) => {
            if (typeof v === 'boolean') return v;
            if (typeof v === 'number' && (v === 0 || v === 1)) return v === 1;
            return undefined;
        };

        let accurate = normalizeAccurate(entry.accurate);
        if (typeof accurate === 'undefined') accurate = normalizeAccurate(entry?.meta?.model_validation?.accurate);

        let message = null;
        if (entry?.message != null && entry.message !== '') {
            message = String(entry.message);
        } else if (entry?.meta?.model_validation?.message != null && entry.meta.model_validation.message !== '') {
            message = String(entry.meta.model_validation.message);
        }

        return { accurate, message };
    }

    const { accurate, message } = getModelValidationInfo(modelValidationLogDataList || []);
    const hasAccurate = typeof accurate === 'boolean';

    return (
        <>
            {popup === 'MODEL_VALIDATION_HISTORY' && <ModelValidationHistoryPopup />}

            {hasAccurate && <BooleanIndicator label='Accurate' value={accurate} />}

            {message && <ValueIndicator label={message} labelOnly labelStyle='text-6' negative />}

            {hasAccurate && <Button label='Show' onClick={() => modelValidationData?.whatIf && history.push('/what-if/edit/' + modelValidationData.whatIf + '/M' + modelValidationData.modelValidationLog + '&TS' + ts)} />}
            <Button label='Details' onClick={() => setSidebar('MODEL_VALIDATION_LOG')} />
            <Button label='History' onClick={() => setPopup('MODEL_VALIDATION_HISTORY')} />

            <TsIndicator ts={ts} />
        </>
    );
}

function Optimizer() {
    const { unitOptimizerData, unitOptimizerLogDataList, setSidebar, popup, setPopup, ts } = useOptimizationPipeline();

    const history = useHistory();

    const { baseText, bestText, gainText, percentText, isGainPositive, isGainNegative } = useMemo(() => {
        const format4 = n => (Number.isFinite(n) ? n.toFixed(4) : '-');
        const toDateKey = x => String(x || '').slice(0, 10);
        const dateKey = toDateKey(ts);
        const selectedLogEntry = (unitOptimizerLogDataList || []).find(e => toDateKey(e?.ts) === dateKey) || null;
        const baseObjective = selectedLogEntry?.base_objective;
        const bestObjective = selectedLogEntry?.best_objective;
        const absoluteGain = Number.isFinite(baseObjective) && Number.isFinite(bestObjective) ? bestObjective - baseObjective : NaN;
        const percentGain = Number.isFinite(baseObjective) && baseObjective !== 0 && Number.isFinite(absoluteGain) ? (absoluteGain / baseObjective) * 100 : NaN;
        const baseText = format4(baseObjective);
        const bestText = format4(bestObjective);
        const gainText = format4(absoluteGain);
        const percentText = Number.isFinite(percentGain) ? `${percentGain >= 0 ? '+' : ''}${percentGain.toFixed(2)}%` : '-';

        return {
            baseText,
            bestText,
            gainText,
            percentText,
            isGainPositive: Number.isFinite(absoluteGain) && absoluteGain > 0,
            isGainNegative: Number.isFinite(absoluteGain) && absoluteGain < 0,
        };
    }, [unitOptimizerLogDataList, ts]);

    return (
        <>
            {popup === 'OPTIMIZER_HISTORY' && <UnitOptimizerHistoryPopup id={unitOptimizerData.unitOptimizerLog} uom={unitOptimizerData?.uom} cumulativeUom={unitOptimizerData?.cumulativeUom} multiplier={unitOptimizerData?.multiplier} onClose={() => setPopup(null)} />}

            <ValueIndicator label='Base' value={baseText} />
            <ValueIndicator label='Best' value={bestText} positive={isGainPositive} negative={isGainNegative} />
            <ValueIndicator label='Gain' value={gainText} positive={isGainPositive} negative={isGainNegative} />
            <ValueIndicator label='Gain %' value={percentText} positive={isGainPositive} negative={isGainNegative} />

            {baseText !== '-' && <Button label='Show' onClick={() => unitOptimizerData?.whatIf && history.push('/what-if/edit/' + unitOptimizerData.whatIf + '/O' + unitOptimizerData.unitOptimizerLog + '&TS' + ts)} />}
            <Button label='Details' onClick={() => setSidebar('OPTIMIZATION_DETAILS')} />
            <Button label='History' onClick={() => setPopup('OPTIMIZER_HISTORY')} />

            <TsIndicator ts={ts} />
        </>
    );
}

function OptimizerInject() {
    return <div className='text-xs'>Optimizer Inject content</div>;
}

function Approve() {
    const { popup, setPopup } = useOptimizationPipeline();

    return (
        <>

            {popup === 'APPROVE' && <ApprovePopup />}

            <Button label='Approve' onClick={() => setPopup('APPROVE')} />

        </>
    );
}

function Run() {
    const { popup, setPopup, edtDynamicCheckLogData } = useOptimizationPipeline();

    const historyItems = useMemo(() => (
        Array.isArray(edtDynamicCheckLogData) ? edtDynamicCheckLogData : []
    ), [edtDynamicCheckLogData]);

    const currentItem = useMemo(() => {
        if (!historyItems.length) return null;

        return [...historyItems].sort((a, b) => {
            const ta = a?.ts ? new Date(a.ts).getTime() : 0;
            const tb = b?.ts ? new Date(b.ts).getTime() : 0;
            return tb - ta;
        })[0];
    }, [historyItems]);

    const isDynamicCheckOk = currentItem
        ? currentItem.finished === true && currentItem.converged === true && currentItem.alarm === false
        : undefined;

    const isBaseCaseSucceeded = currentItem
        ? currentItem.converged === true
        : undefined;

    const isBestCaseSucceeded = currentItem
        ? currentItem.finished === true
        : undefined;

    return (
        <>

            {console.log(popup)}

            {popup === 'DYNAMIC_VALIDATION' && <DynamicValidationPopup />}
            {popup === 'DYNAMIC_VALIDATION_SETTINGS' && <DynamicValidationSettingsPopup />}
            {popup === 'DYNAMIC_VALIDATION_DETAILS' && <DynamicValidationDetailsPopup data={currentItem} onClose={() => setPopup(null)} />}
            {popup === 'DYNAMIC_VALIDATION_HISTORY' && <DynamicValidationHistoryPopup data={historyItems} onClose={() => setPopup(null)} />}

            <BooleanIndicator label='Dynamic Check OK' value={isDynamicCheckOk} />
            <BooleanIndicator label='Base Case Succeeded' value={isBaseCaseSucceeded} />
            <BooleanIndicator label='Best Case Succeeded' value={isBestCaseSucceeded} />

            <Button label='Run' onClick={() => setPopup('DYNAMIC_VALIDATION_SETTINGS')} />
            <Button label='Details' onClick={() => setPopup('DYNAMIC_VALIDATION_DETAILS')} disabled={!currentItem} />
            <Button label='History' onClick={() => setPopup('DYNAMIC_VALIDATION_HISTORY')} disabled={!historyItems.length} />

            {currentItem && <TsIndicator ts={currentItem.ts} />}

        </>
    );
}

export function Button(props) {
    return (
        <button className={'transition rounded-full text-xs font-medium text-center px-16 py-8 ' + (props.disabled ? 'bg-gray-200 text-gray-500 cursor-default' : 'bg-blue-gray-900 hover:bg-black text-white')} onClick={props.onClick} disabled={props.disabled}>
            {props.label}
        </button>
    );
}

function BooleanIndicator(props) {
    const stateClassName = 'flex-1 lg:flex-none rounded-full shadow-xl px-8 py-4 flex items-center justify-center';

    return (
        <div className='w-full flex flex-col lg:flex-row items-center gap-4 bg-white shadow-inner rounded-xl lg:rounded-full border text-11 font-semibold pl-5 lg:pl-12 pr-5 py-4'>
            <div className='flex-1'>{props.label}</div>
            <div className='w-full lg:w-auto flex items-center gap-4 text-10'>
                <div className={stateClassName + (props.value === true ? (props.reversed ? ' bg-red-100 text-red-900' : ' bg-green-100 text-green-900') : ' border')}>Yes</div>
                <div className={stateClassName + (props.value === false ? (props.reversed ? ' bg-green-100 text-green-900' : ' bg-red-100 text-red-900') : ' border')}>No</div>
            </div>
        </div>
    );
}

function ValueIndicator(props) {
    const stateClassName = 'flex-1 lg:flex-none rounded-full shadow-xl px-10 py-4 flex items-center justify-center border';

    let variantClass = '';
    if (props.positive) {
        variantClass = 'bg-green-100 text-green-900';
    } else if (props.negative) {
        variantClass = 'bg-red-100 text-red-900';
    } else if (props.warning) {
        variantClass = 'bg-amber-100 text-amber-900';
    }

    const containerBaseClass = 'w-full flex flex-col lg:flex-row items-center gap-4 shadow-inner rounded-xl border text-11 font-semibold ' + (props.labelOnly ? 'py-6 pl-10 pr-10' : 'py-4 pl-5 lg:pl-12 pr-5 lg:pr-5 lg:rounded-full');
    const containerClassName = containerBaseClass + ' ' + (props.labelOnly ? variantClass : 'bg-white');

    return (
        <div className={containerClassName}>
            <div className={'flex-1 ' + (props.labelStyle || '')}>{props.label}</div>
            {!props.labelOnly && (
                <div className='w-full lg:w-auto flex items-center gap-4 text-10'>
                    <div className={stateClassName + ' text-center lg:text-right ' + variantClass}>
                        {props.value || '-'}
                    </div>
                </div>
            )}
        </div>
    );
}

function TsIndicator(props) {
    return (
        <div className='flex items-center justify-center gap-8 text-10 px-12'>
            <FaCalendar className='opacity-25' />
            {props.ts ? format(new Date(props.ts), 'yyyy.MM.dd.', { locale: hu }) : '-'}
        </div>
    );
}

const useHeaderDateTimeStyles = makeStyles(() => ({
    datePickerRoot: {
        '& .MuiOutlinedInput-root.Mui-focused': {
            backgroundColor: '#ffffff !important'
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-input': {
            backgroundColor: '#ffffff'
        },
        '& .MuiOutlinedInput-adornedEnd': {
            paddingRight: 0
        }
    },
    datePickerInput: {
        padding: '20px 0px 20px 20px'
    },
    datePickerAdornment: {
        marginRight: 0,
        '& .MuiIconButton-root': {
            padding: 0,
            marginRight: 0,
        }
    }
}));

const HeaderDateTextField = forwardRef(function HeaderDateTextField(props, ref) {
    const { pickerClasses, inputRef, InputProps, className, ...rest } = props;

    return (
        <TextField
            {...rest}
            ref={ref}
            inputRef={inputRef}
            size='small'
            className={(className ? className + ' ' : '') + pickerClasses.datePickerRoot}
            InputProps={{
                ...(InputProps || {}),
                classes: {
                    ...(InputProps && InputProps.classes ? InputProps.classes : {}),
                    input: pickerClasses.datePickerInput,
                    adornedEnd: pickerClasses.datePickerAdornment
                }
            }}
        />
    );
});

function HeaderDateTime({ value, onChange }) {
    const { timestamps } = useOptimizationPipeline();
    const classes = useHeaderDateTimeStyles();

    const onChangeRef = useRef(onChange);
    useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

    const toLocalMidnightDate = (v) => {
        if (!v) return null;
        if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
        if (typeof v === 'number') {
            const d = new Date(v);
            return new Date(d.getFullYear(), d.getMonth(), d.getDate());
        }
        if (typeof v === 'string') {
            const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v);
            if (!m) return null;
            const [, y, mo, d] = m;
            return new Date(Number(y), Number(mo) - 1, Number(d));
        }
        return null;
    };

    const toNaiveIsoMidnight = (d) => {
        if (!d) return null;
        const dd = d instanceof Date ? d : toLocalMidnightDate(d);
        if (!dd || Number.isNaN(dd.getTime())) return null;
        const y = dd.getFullYear();
        const m = String(dd.getMonth() + 1).padStart(2, '0');
        const day = String(dd.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}T00:00:00.000000`;
    };

    const currentTsString = useMemo(() => {
        if (typeof value === 'string') return value;
        const iso = toNaiveIsoMidnight(value);
        return iso ?? '';
    }, [value]);

    const currentDateValue = useMemo(() => {
        if (!value && !currentTsString) return null;
        if (typeof value === 'string' || typeof value === 'number' || value instanceof Date) return toLocalMidnightDate(value);
        if (currentTsString) return toLocalMidnightDate(currentTsString);
        return null;
    }, [value, currentTsString]);

    const sortedTimestamps = useMemo(() => (
        Array.isArray(timestamps) ? [...timestamps].filter(Boolean).sort((a, b) => b.localeCompare(a)) : []
    ), [timestamps]);

    const timestampSet = useMemo(() => new Set(sortedTimestamps), [sortedTimestamps]);

    const { allowedDateKeys, dateKeyToTsMap } = useMemo(() => {
        const map = {};
        const keySet = new Set();

        sortedTimestamps.forEach((ts) => {
            if (typeof ts !== 'string') return;
            const dateKey = ts.slice(0, 10);
            if (!dateKey) return;
            keySet.add(dateKey);
            const prev = map[dateKey];
            if (!prev || ts > prev) map[dateKey] = ts;
        });

        return { allowedDateKeys: keySet, dateKeyToTsMap: map };
    }, [sortedTimestamps]);

    const disabled = sortedTimestamps.length === 0;

    const shouldDisableDate = useCallback((date) => {
        if (!date || disabled) return true;
        const dateKey = format(date, 'yyyy-MM-dd');
        return !allowedDateKeys.has(dateKey);
    }, [disabled, allowedDateKeys]);

    useEffect(() => {
        if (disabled) return;
        const newest = sortedTimestamps[0];
        if (!currentTsString || !timestampSet.has(currentTsString)) onChangeRef.current(newest);
    }, [disabled, sortedTimestamps, currentTsString, timestampSet]);

    const handleDateChange = useCallback((date) => {
        if (!date || Number.isNaN(date.getTime())) {
            onChangeRef.current(null);
            return;
        }

        const dateKey = format(date, 'yyyy-MM-dd');
        const tsForDay = dateKeyToTsMap[dateKey];

        if (tsForDay) onChangeRef.current(tsForDay);
        else onChangeRef.current(null);
    }, [dateKeyToTsMap]);

    const renderTextField = useCallback((props) => (
        <HeaderDateTextField {...props} pickerClasses={classes} />
    ), [classes]);

    return (
        <div style={{ width: 160 }}>
            <MuiPickersUtilsProvider utils={DateFnsUtils} locale={hu}>
                <KeyboardDatePicker
                    disableToolbar
                    variant='inline'
                    inputVariant='outlined'
                    format='yyyy.MM.dd.'
                    value={currentDateValue}
                    onChange={handleDateChange}
                    disabled={disabled}
                    shouldDisableDate={shouldDisableDate}
                    autoOk
                    label='Date'
                    InputLabelProps={{ shrink: true }}
                    TextFieldComponent={renderTextField}
                />
            </MuiPickersUtilsProvider>
        </div>
    );
}
